import axiosClient from 'apis/axios-client';
import { AxiosResponse } from 'axios';
import { formatUtcStringToLocal } from 'pages/Admin/giveawayManagement/giveawayFormUtils';

const GET_USERS_URL = '/AdminUser/GetUsers';
const UPDATE_USER_STATUS_URL = '/AdminUser/UpdateUserStatus';
const CREDIT_TOKENS_URL = '/AdminUser/CreditTokens';
const GET_USER_DETAILS_URL = '/AdminUser/GetUserDetails';
const GET_OG_USER_GROUPS_URL = '/AdminUser/GetOgUserGroups';
const ADD_OG_USER_GROUP_URL = '/AdminUser/AddOgUserGroup';
const UPDATE_OG_USER_GROUP_URL = '/AdminUser/UpdateOgUserGroup';
const DELETE_OG_USER_GROUP_URL = '/AdminUser/DeleteOgUserGroup';
const MOVE_USERS_TO_OG_GROUP_URL = '/AdminUser/MoveUsersToOgGroup';

/**
 * Body for AddOgUserGroup / UpdateOgUserGroup.
 *
 * On add, `roleName` is required. On update every field is optional — send only
 * what changed: omitting `freeTokenAmount` keeps the current value, while
 * `setFreeTokenAmountUnlimited: true` clears it back to "Unlimited" (null).
 *
 * `userCount` is a pending backend addition — the Phase 1 handoff has it as a
 * read-only computed field, and the endpoints will ignore it until the backend
 * ships support. Left in so the form is ready; confirm the field name against
 * the API before relying on it.
 */
export interface OgUserGroupPayload {
  roleName?: string;
  description?: string;
  userCount?: number;
  freeTokenAmount?: number | null;
  setFreeTokenAmountUnlimited?: boolean;
}

/**
 * The message the backend wants shown for a failed admin call. These endpoints
 * put a human-readable string in `message` and ModelState failures put the
 * field errors in `errors` — both beat a generic fallback.
 */
export const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;

  if (typeof data?.message === 'string' && data.message.trim()) {
    return formatUtcStringToLocal(data.message);
  }

  if (data?.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors as Record<string, string[]>)
      .flat()
      .find((message) => typeof message === 'string' && message.trim());
    if (first) return formatUtcStringToLocal(first);
  }

  // Timeout / offline / rate-limit errors are rewritten by the axios client and
  // already carry a sentence worth showing.
  if (error?.isTimeout || error?.isNetworkError || error?.isRateLimited) {
    return formatUtcStringToLocal(error.message as string);
  }

  return fallback;
};

/**
 * GetUsers serves every User Management tab, in one of two modes.
 *
 * **With `roleId`** the page is taken over that group alone, and the rows come
 * back flat — this is what the tabs use, so page 1 of a tab is page 1 of its
 * own group:
 *
 *   { items: [user, ...], totalCount: <that group's size>, page, pageSize,
 *     roleCounts: [{ roleId, roleName, count }] }
 *
 * **Without `roleId`** the page is taken across ALL users and only then split
 * by role, so a group's rows are scattered over the same pages as everybody
 * else's:
 *
 *   { items: [{ users: [...], lifetimeOgUsers: [...], ogUsers: [...] }], ... }
 *
 * The buckets are disjoint — a user in an OG group is not repeated under
 * `users`, even though `userRoles` still lists the base "user" role for them.
 * `roleCounts` comes back either way and is what the tab chips read.
 *
 * Read the rows through `getUsersByRole` / `getAllUsers` rather than touching
 * `items` — they handle both shapes, and in the bucketed one the keys are
 * derived from role names, so an admin-created group produces a key we cannot
 * predict.
 */
export const getUsers = async (
  searchTerm: string,
  filter: string,
  page: number,
  size: number,
  roleId?: number | null
): Promise<any> => {
  try {
    let url = `${GET_USERS_URL}?searchTerm=${encodeURIComponent(searchTerm)}&filter=${encodeURIComponent(filter)}&page=${page}&size=${size}`;
    if (roleId !== null && roleId !== undefined) {
      url += `&roleId=${encodeURIComponent(roleId)}`;
    }

    const response: AxiosResponse<any> = await axiosClient.get(url);
    return response; // Return the entire response
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error fetching users:', error.message);
    } else {
      console.error('Unknown error fetching users');
    }
    throw error;
  }
};


/** The role every user carries; on its own it means "no OG group". */
export const BASE_USER_ROLE_NAME = 'user';

/** Role names reach us as both keys ("lifetimeOgUsers") and labels ("Lifetime OG Users"). */
const normalizeRoleName = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Holds rows that arrived with no role bucket around them. */
const UNBUCKETED = '';

/**
 * The role buckets inside `items`. A `roleId`-scoped response has no buckets at
 * all — its rows are collected under `UNBUCKETED`, since the server has already
 * narrowed them to the one role we asked for.
 */
const getUserBuckets = (response: any): Record<string, any[]> => {
  const items = response?.items ?? response?.Items;
  if (!Array.isArray(items)) return {};

  const buckets: Record<string, any[]> = {};
  const flat: any[] = [];

  items.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    // A bucket container is a plain object of arrays; a user row has a userId.
    if ('userId' in entry || 'UserId' in entry) {
      flat.push(entry);
      return;
    }
    Object.entries(entry).forEach(([key, value]) => {
      if (Array.isArray(value)) buckets[key] = [...(buckets[key] ?? []), ...value];
    });
  });

  if (flat.length) buckets[UNBUCKETED] = flat;
  return buckets;
};

/** Every user on the current page, regardless of which bucket they arrived in. */
export const getAllUsers = (response: any): any[] =>
  Object.values(getUserBuckets(response)).flat();

/**
 * The rows for one tab. `roleName` null means the Users tab — users holding no
 * role beyond the base one.
 *
 * The bucket key is matched loosely against the role name ("Lifetime OG Users"
 * → `lifetimeOgUsers`, "user" → `users`). If no bucket matches — an
 * admin-created group whose key we cannot predict — each row's own `userRoles`
 * is used instead.
 */
export const getUsersByRole = (response: any, roleName: string | null): any[] => {
  const base = normalizeRoleName(BASE_USER_ROLE_NAME);
  const wanted =
    roleName === null ? [base, `${base}s`] : [normalizeRoleName(roleName)];

  const buckets = getUserBuckets(response);
  const keys = Object.keys(buckets);

  // Already scoped by `roleId` — every row belongs to the caller's group.
  if (keys.length === 1 && keys[0] === UNBUCKETED) return buckets[UNBUCKETED];

  const bucketKey = keys.find((key) => wanted.includes(normalizeRoleName(key)));
  if (bucketKey) return buckets[bucketKey];

  return getAllUsers(response).filter((user) => {
    const roles: string[] = Array.isArray(user?.userRoles) ? user.userRoles : [];
    const groups = roles.filter((role) => normalizeRoleName(role) !== base);
    return roleName === null
      ? groups.length === 0
      : groups.some((role) => normalizeRoleName(role) === normalizeRoleName(roleName));
  });
};

export const getUserDetails = async (userId: number): Promise<any> => {
  try {
    const url = `${GET_USER_DETAILS_URL}/${userId}`;

    const response: AxiosResponse<any> = await axiosClient.get(url);
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error fetching user details:', error.message);
    } else {
      console.error('Unknown error fetching user details');
    }
    throw error;
  }
};


export const creditTokens = async (
  userIds: number[],
  tokensToCredit: number
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(
      CREDIT_TOKENS_URL,
      { userIds, tokensToCredit }
    );
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error crediting tokens:', error.message);
    } else {
      console.error('Unknown error crediting tokens');
    }
    throw error;
  }
};


/**
 * Every configurable OG user group with its live member count. Feeds both the
 * Manage Users Roles modal and the counts on the User Management tab bar.
 *
 * Unlike the other calls here this one returns the rows themselves, not the
 * response object: the groups arrive inside the `{ isSuccess, message, data }`
 * envelope, and unwrapping once here keeps every caller from repeating it.
 */
export const getOgUserGroups = async (): Promise<any[]> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_OG_USER_GROUPS_URL
    );

    if (Array.isArray(response)) return response;

    const rows = (response as any)?.data ?? (response as any)?.Data;
    return Array.isArray(rows) ? rows : [];
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error fetching OG user groups:', error.message);
    } else {
      console.error('Unknown error fetching OG user groups');
    }
    throw error;
  }
};


export const addOgUserGroup = async (
  payload: OgUserGroupPayload
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(
      ADD_OG_USER_GROUP_URL,
      payload
    );
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error adding OG user group:', error.message);
    } else {
      console.error('Unknown error adding OG user group');
    }
    throw error;
  }
};


export const updateOgUserGroup = async (
  id: number,
  payload: OgUserGroupPayload
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.put(
      `${UPDATE_OG_USER_GROUP_URL}/${id}`,
      payload
    );
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error updating OG user group:', error.message);
    } else {
      console.error('Unknown error updating OG user group');
    }
    throw error;
  }
};


/**
 * Delete an OG user group. Only an empty one can go:
 *
 *   200  deleted
 *   404  no such group, or a protected system role ("admin" / "user")
 *   409  the group still has members; the body carries
 *        `data.assignedUserCount` alongside a `message` worth showing as-is
 *
 * So a 409 is a normal outcome to report, not a failure to swallow — read
 * `getDeleteBlockedCount` off the error before falling back to a generic
 * message.
 */
export const deleteOgUserGroup = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.delete(
      `${DELETE_OG_USER_GROUP_URL}/${id}`
    );
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error deleting OG user group:', error.message);
    } else {
      console.error('Unknown error deleting OG user group');
    }
    throw error;
  }
};


/**
 * The `assignedUserCount` a 409 from DeleteOgUserGroup reports, or null when
 * the error was anything else.
 */
export const getDeleteBlockedCount = (error: any): number | null => {
  if (error?.response?.status !== 409) return null;

  const body = error?.response?.data;
  const count = Number(
    body?.data?.assignedUserCount ??
      body?.Data?.AssignedUserCount ??
      body?.assignedUserCount
  );
  return Number.isFinite(count) ? count : null;
};


/**
 * Move users into an OG group with a list of user IDs, or out of every OG group
 * when `roleId` is null.
 *
 * Request body: { userIds: number[], roleId: number | null }
 */
export const moveUsersToOgGroupApi = async (
  userIds: number[],
  roleId: number | null
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(
      MOVE_USERS_TO_OG_GROUP_URL,
      { userIds, roleId }
    );
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error moving users to OG group:', error.message);
    } else {
      console.error('Unknown error moving users to OG group');
    }
    throw error;
  }
};

export const moveUserToOgGroup = async (
  userId: number,
  roleId: number | null
): Promise<any> => {
  return moveUsersToOgGroupApi([userId], roleId);
};


export const updateUserStatus = async (
  userId: number,
  isBlocked: boolean,
  comment: string
): Promise<any> => {
  try {
    const payload = {
      userId,
      isBlocked,
      comment,
    };

    const response: AxiosResponse<any> = await axiosClient.post(
      UPDATE_USER_STATUS_URL,
      payload
    );

    return response; // Return the entire response
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error updating user status:', error.message);
    } else {
      console.error('Unknown error updating user status');
    }
    throw error;
  }
};
